import React, { useContext, useEffect, useRef, useState } from 'react';
import Amap from '@/common/components/AMap';
import styles from './index.module.less';
import { ModelClusterInfo, ShopItem } from '@/common/components/business/ExpandStore/CircleBasicInfo/ts-config';
import { debounce, isArray, isNotEmptyAny } from '@lhb/func';
import { markerActiveColorOption } from '@/views/recommend/pages/industrycircle/ts-config';
import ReactDOM from 'react-dom';
import IconFont from '@/common/components/IconFont';
import { useHoverMarker, useHoverChanceMarker } from './config';
import { Typography } from 'antd';
import { CircleBusinessZoom, TargetZoom } from '@/common/components/business/ExpandStore/CircleBusinessMap/ts-config';
import { ChanceStatus, ProtectString } from '@/common/components/business/ExpandStore/ts-config';
import { v4 } from 'uuid';
import UserInfoContext from '@/layout/context';
import { RelateChancePoint } from '../CircleBasicInfo/ts-config';

interface CircleTaskDetailMapProps{
  businessDetail:{
    modelClusterInfo:ModelClusterInfo;
    [k:string]:any
  }
  chancePointList: RelateChancePoint[];
}

/** 拓店任务详情-基本信息-顶部地图 */
const CircleTaskDetailMap:React.FC<CircleTaskDetailMapProps> = (props) => {
  const { businessDetail, chancePointList } = props;
  const { modelClusterInfo } = businessDetail;
  const [mapIns, setMapIns] = useState<any>(null);// 地图实例
  const overLayers = useRef<any>(null);
  const chanceOverLayers = useRef<any>(null);
  const { createHoverMarker, removeHoverMarker } = useHoverMarker(mapIns);
  const { createHoverChanceMarker, removeHoverChanceMarker } = useHoverChanceMarker(mapIns);
  const [random, setRandom] = useState<string>(''); // 嵌套展示抽屉，被多次使用到时需要区分不同抽屉，否则getElementById拿到的是其他抽屉的dom元素
  const { tenantInfo: { logo: tenantLogo } } = useContext(UserInfoContext); // 租户信息

  useEffect(() => {
    const rdm = v4();
    setRandom(rdm);
  }, []);

  useEffect(() => {
    if (!mapIns) return;
    if (isNotEmptyAny(businessDetail.modelClusterInfo)) {
      const { modelClusterInfo } = businessDetail;
      flyToCurrentPos(modelClusterInfo.centerLng, modelClusterInfo.centerLat);
      createBusinessLayers(modelClusterInfo);
      createSurroundMarkers(modelClusterInfo);
    }
  }, [mapIns]);

  useEffect(() => {
    if (!mapIns) return;
    createChanceListMarkers(isArray(chancePointList) ? chancePointList : []);
  }, [chancePointList, mapIns]);

  // 创建商圈和marker
  const createBusinessLayers = (businessInfo:ModelClusterInfo) => {
    if (overLayers.current) {
      mapIns.remove(overLayers.current);
      overLayers.current = null;
    }
    const markers: any = []; // 围栏覆盖物
    const { radius, polygon } = businessInfo;
    let marker;
    if (radius) {
      marker = createBusinessCircle(businessInfo); // 绘制商圈围栏（圆）覆盖物
    } else if (isArray(polygon) && polygon.length) {
      marker = createBusinessPolygon(businessInfo);
    }
    markers.push(...marker);
    const railOverlayGroups = new window.AMap.OverlayGroup(markers); // 围栏覆盖物群组
    mapIns.add(railOverlayGroups); // 添加到地图
    marker[0] && mapIns.setFitView(marker[0]); // 视口自适应

    const elementId = `${random}${businessInfo.modelClusterId}`;
    renderBusinessName(elementId, businessInfo.modelClusterName);
    overLayers.current = railOverlayGroups;
  };

  // 创建周边500米门店marker
  const createSurroundMarkers = (businessInfo:ModelClusterInfo) => {
    if (!isNotEmptyAny(businessInfo?.shopList)) return;

    const surroundMarkerList:Array<any> = [];
    businessInfo.shopList.forEach((item) => {
      const elementId = `${random}${item.id}`;
      const marker = new window.AMap.Marker({
        zooms: [CircleBusinessZoom.MinZomm, 20],
        anchor: 'center',
        position: new window.AMap.LngLat(+item.lng, +item.lat),
        content: `<div id=${elementId}></div>`
      });
      mapIns.add(marker); // 添加到地图
      renderSurroundMarker(elementId, item);
      surroundMarkerList.push(marker);
      marker.on('mousemove', () => mouseMoveMarker(item));
      marker.on('mouseout', () => mouseOutMarker());
    });

  };
  // 创建多边形商圈
  const createBusinessPolygon = (item:ModelClusterInfo) => {
    const { centerLng, centerLat } = item;
    if (!(+centerLng && +centerLat)) return;
    const lnglat = [+centerLng, +centerLat];
    // const path = item?.polygon?.map((item) => {
    //   return [+item.lng, +item.lat];
    // });
    const fillColor = markerActiveColorOption;
    const polygon = new window.AMap.Polygon({
      zooms: [CircleBusinessZoom.MinZomm, 20],
      path: item?.polygon,
      strokeOpacity: 1,
      fillOpacity: 0.2,
      strokeWeight: 1,
      strokeStyle: 'dashed',
      // zIndex: 60,
      anchor: 'bottom-center',
      bubble: true,
      extData: {
        id: item.modelClusterId
      },
      ...fillColor
    });
    const elementId = `${random}${item.modelClusterId}`;
    const marker = new window.AMap.Marker({
      zooms: [CircleBusinessZoom.MinZomm, 20],
      position: lnglat,
      anchor: 'center',
      extData: item,
      content: `<div id="${elementId}"></div>`,
    });


    return [polygon, marker];
  };

  // 创建圆形商圈
  const createBusinessCircle = (item: ModelClusterInfo) => {
    const { centerLng, centerLat } = item;
    if (!(+centerLng && +centerLat)) return;
    const lnglat = [+centerLng, +centerLat];
    const fillColor = markerActiveColorOption;
    const circle = new window.AMap.Circle({
      zooms: [CircleBusinessZoom.MinZomm, 20],
      center: lnglat,
      radius: item?.radius, // 半径（米）
      strokeWeight: 1,
      strokeOpacity: 1,
      fillOpacity: 0.2,
      strokeStyle: 'dashed',
      strokeDasharray: [10, 10],
      bubble: true, // 为了支持在商圈围栏上测距
      extData: item,
      ...fillColor,
    });
    const elementId = `${random}${item.modelClusterId}`;
    const marker = new window.AMap.Marker({
      position: lnglat,
      zooms: [CircleBusinessZoom.MinZomm, 20],
      anchor: 'center',
      extData: item,
      content: `<div id="${elementId}"></div>`,
    });
    return [circle, marker];
  };

  /**
   * @description 关联机会点markers（所有机会点都有icon、一部分有区域保护圈）
   * @param list
   */
  const createChanceListMarkers = (list: RelateChancePoint[]) => {
    if (chanceOverLayers.current) {
      mapIns.remove(chanceOverLayers.current);
      chanceOverLayers.current = null;
    }
    if (!isArray(list) || !list.length) return;

    let markers: any[] = [];
    isArray(list) && list.filter((itm: RelateChancePoint) => itm.status !== ChanceStatus.Complete).forEach((itm: any) => {
      const arr: any[] = createChance(itm);
      markers = markers.concat(arr);
    });
    const overlayGroup = new window.AMap.OverlayGroup(markers);
    mapIns.add(overlayGroup);
    chanceOverLayers.current = overlayGroup;
  };

  const createChance = (item: RelateChancePoint) => {
    const { lng, lat, protectInfo } = item;
    if (!(+lng && +lat)) return [];
    const lnglat = [+lng, +lat];

    const markers: any[] = [];
    // 机会点保护圈
    const isProtected = protectInfo?.protectRadius && protectInfo?.statusName === ProtectString.On;
    if (isProtected) {
      const circle = new window.AMap.Circle({
        zooms: [CircleBusinessZoom.MinZomm, 20],
        center: lnglat,
        radius: protectInfo.protectRadius, // 半径（米）
        strokeColor: '#F53F3F',
        fillColor: '#F53F3F',
        strokeWeight: 2,
        strokeOpacity: 1,
        fillOpacity: 0.14,
        strokeStyle: 'dashed',
        strokeDasharray: [10, 10],
        bubble: true, // 为了支持在商圈围栏上测距
        extData: item,
      });
      markers.push(circle);
    }

    // 机会点icon
    const elementId = `${random}${item.id}`;
    const marker = new window.AMap.Marker({
      zooms: [CircleBusinessZoom.MinZomm, 20],
      anchor: 'center',
      position: new window.AMap.LngLat(+item.lng, +item.lat),
      content: `<div id=${elementId}></div>`,
      zIndex: 10,
    });
    mapIns.add(marker); // 添加到地图
    renderChanceMarker(elementId);
    markers.push(marker);
    marker.on('mousemove', () => mouseMoveChanceMarker(item));
    marker.on('mouseout', () => mouseOutChanceMarker());

    markers.push(marker);
    return markers;
  };

  // 机会点处理
  const mouseMoveChanceMarker = debounce((info) => {
    createHoverChanceMarker(info);
  }, 0);
  const mouseOutChanceMarker = debounce(() => {
    removeHoverChanceMarker();
  }, 0);

  const renderChanceMarker = (elementId) => {
    const Node = <div className={styles.box}>
      <img className={styles.outerImg} src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/map/icon_outer.png`}/>
      <img className={styles.innerImg} src={tenantLogo || `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/map/icon_store_default.png`}/>
    </div>;
    ReactDOM.render(Node, document.getElementById(elementId));
  };

  // 门店相关

  const mouseMoveMarker = debounce((info) => {
    createHoverMarker(info);
  }, 0);
  const mouseOutMarker = debounce(() => {
    removeHoverMarker();
  }, 0);

  const renderSurroundMarker = (elementId, item:ShopItem) => {
    const Node = <div className={styles.box}>
      <img className={styles.outerImg} src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/map/icon_outer.png`}/>
      <img className={styles.innerImg} src={item?.logo || `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/map/icon_store_default.png`}/>
    </div>;
    ReactDOM.render(Node, document.getElementById(elementId));
  };

  // 商圈
  const renderBusinessName = (elementId, info) => {
    const Node = <div className={styles.markerWrapper}>
      <div
        className={styles.markerContent} >
        <Typography.Text style={{ maxWidth: 165, color: '#fff', fontSize: 12 }} ellipsis={{ tooltip: info }}>{info}</Typography.Text>
      </div>
      <div className={styles.point} />
    </div>;
    ReactDOM.render(Node, document.getElementById(elementId));
  };

  const flyToCurrentPos = (lng, lat) => {
    mapIns && mapIns.setZoomAndCenter(TargetZoom, [+lng, +lat], true);
  };
  const handleClickPosition = () => {
    if (!isNotEmptyAny(modelClusterInfo)) return;
    const { centerLat, centerLng } = modelClusterInfo;
    flyToCurrentPos(centerLng, centerLat);
  };
  // 地图加载完成
  const mapLoadedHandle = (ins: any) => {
    ins && setMapIns(ins);
  };

  return <div className={styles.circleTaskDetailMapWrapper}>
    <Amap
      mapOpts={{
        // zooms: [3, 16],
      }}
      loaded={mapLoadedHandle}
      plugins={[
        'AMap.DistrictSearch',
        'AMap.PlaceSearch',
      ]}
    />

    <div className={styles.toolBottomBar}>
      <IconFont
        iconHref='iconic_mendiangengzhong'
        className='c-666'
        onClick={handleClickPosition}
      />
    </div>


  </div>;
};

export default CircleTaskDetailMap;
